/** @format */

import { Card, Col, Menu, Row, Button, Spin } from "antd";
import axios from "axios";
import Link from "next/link";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import { convert } from "html-to-text";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import type { Article, Articles } from "../../../interfaces/articles";
import responsiveColumn from "../../../methods/responsiveColumn";
import CustomBreadcrumb from "../../components/CustomBreadcrumb";
import Footer from "../../components/Footer";
import { getStaticProps } from "../../../methods/i18n";
import type { GetStaticPaths } from "next";
// import Head from "next/head";
import SEO from "../../components/seo";
import { useTranslation } from "next-i18next";
import { HeadScriptTag } from "../../components/HeadScriptTag";
import type { CustomMenuItemType } from "../../../interfaces/menu";
import Custom404 from "../404";
import { MarkDownComp } from "../../components/practice_areas/tax-compliance/MarkDownComp/MarkDownComp";
import type { DefaultTFuncReturn } from "i18next";
import type { StaticBoxProps } from "../../../interfaces/globalProps";
import { replaceSpacesAndHyphensWithUnderscores } from "../../components/utils";

export { getStaticProps };

export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export default function PracticeArea() {
  const [areas, setAreas] = useState<Articles>();
  const [article, setArticle] = useState<Article>();
  const [selectedArea, setSelectedArea] = useState<StaticBoxProps | null>(null);
  const [showCustom404, setShowCustom404] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    locale,
    query: { slug },
  } = useRouter();

  const { t } = useTranslation([
    "breadcrumb_section",
    "bankruptcy",
    "meta_tags",
  ]);

  const setQuery = useMemo(() => {
    if (locale !== "en") {
      return `?lang=${locale}&`;
    } else {
      return "?";
    }
  }, [locale]);

  const fetchAreas = async () => {
    try {
      setIsLoading(true);
      const r = await axios({
        url: `https://wp.taxworkoutgroup.com/wp-json/wp/v2/practice_areas${setQuery}_embed&categories=14`,
        method: "get",
      });
      setAreas(
        r.data?.sort((a: any, b: any) => a.acf.priority - b.acf.priority)
      );
      const foundArticle: Article | undefined = (r.data as Articles)?.find(
        (article) => article.slug === slug
      );
      setArticle(foundArticle);

      // Check if foundArticle is undefined or null, indicating no data found
      if (!foundArticle) {
        setShowCustom404(true);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
      console.log(selectedArea);
    }
  };

  useEffect(() => {
    fetchAreas();
  }, [locale, slug]);

  useEffect(() => {
    if (article) {
      setSelectedArea({
        wordPressID: article.id,
        title: article.title.rendered,
        slug: article.slug,
      });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [article]);

  useEffect(() => {
    //FIND ALL LIs
    const lis = document.querySelectorAll("li");
    lis?.forEach((li) => {
      if (li.style.listStyleType === "none") li.classList.add("no-before");
    });
  }, [selectedArea]);

  const [locationtypeData, setLocationTypeData] = useState<any>();
  const [locationData, setLocationData] = useState<any>();
  const [isMapMounted, setIsMapMounted] = useState<any>(true);

  const [modifiedLocationTitle, setModifiedLocationTitle] = useState<string>("");
  const [cleanedBreadcrumbLabel, setCleanedBreadcrumbLabel] = useState<string>("");

  useEffect(() => {
    if (slug) {
      const modified = replaceSpacesAndHyphensWithUnderscores(slug as string);
      setModifiedLocationTitle(modified);
    }
  }, [slug]);

  useEffect(() => {
    if (locationData?.length > 0) {
      const filteredLocationsData = locationData?.find(
        (locationValue: any) => locationValue?.slug === slug
      );
      setLocationTypeData(filteredLocationsData);
      setIsMapMounted(false);
    }
  }, [slug, locationData]);

  useEffect(() => {
    let timer: any;
    if (!isMapMounted) {
      timer = setTimeout(() => setIsMapMounted(true), 500);
    }
    return () => clearTimeout(timer);
  }, [isMapMounted]);

  const modifiedLocation: string = replaceSpacesAndHyphensWithUnderscores(
    slug as string
  );

  // const breadcrumbLabel = selectedArea
  // 	? t(`${modifiedLocation}`, { ns: "meta_tags" })
  // 	: "Tax Motivated Bankruptcy Filings";

  useEffect(() => {
    const title = selectedArea ? t(`${modifiedLocation}`, { ns: "meta_tags" }) : t("home");
    const description = selectedArea
      ? t(`${modifiedLocation}_desc`, { ns: "meta_tags" })
      : t("home_desc");
  }, [modifiedLocation, selectedArea]);

  const title = selectedArea
    ? `${selectedArea.title} | Tax Bankruptcy | Practice Areas | TaxWorkoutGroup`
    : t("meta_tags:home");
  const description = t("meta_tags:home_desc");
  const keywords = "Tax,Tax Bankruptcy ,Tax Controversy";
  // const ogImage = "https://www.example.com/static/images/og-image.jpg";
  const ogType = "website";
  const canonicalURL = selectedArea
    ? `https://taxworkoutgroup.com/taxbankruptcy/${selectedArea?.slug as string
    }`
    : "https://www.taxworkoutgroup.net";

  const menuItems: CustomMenuItemType[] | undefined = areas?.map((item) => {
    return {
      label: (
        <Link
          style={{ fontSize: 16 }}
          href={`/taxbankruptcy/${item.slug}`}
          onClick={() => {
            if (selectedArea?.slug !== item.slug) setIsLoading(true);
          }}
        >
          {item?.title.rendered}
        </Link>
      ),
      key: item.slug as string,
      style: {
        height: "auto",
        lineHeight: 1.6,
        padding: "12px 24px",
        whiteSpace: "normal",
      },
      type: "item",
    };
  });

  if (showCustom404) {
    return <Custom404 />;
  }

  return (
    <>
      <SEO
        title={selectedArea ? t(`${modifiedLocation}`, { ns: "meta_tags" }) : t("home")}
        description={selectedArea ? t(`${modifiedLocation}_desc`, { ns: "meta_tags" }) : t(description)}
        keywords={keywords}
        ogType={ogType}
        canonicalURL={canonicalURL}
      />
      <HeadScriptTag />
      <CustomBreadcrumb
        items={[
          {
            label: t("breadcrumb_section:home"),
            href: "/",
          },
          {
            label: t("bankruptcy:practice"),
            href: "/practice-areas",
          },
          {
            label: t("bankruptcy:tax_bankruptcy"),
            href: "/taxbankruptcy",
          },
          {
            label: `${selectedArea?.title ?? ""}`,
            href: `/taxbankruptcy/${selectedArea?.slug as string}`,
          },
        ]}
      />
      <Spin spinning={isLoading}>
        <div style={{ minHeight: "calc(1.5vw + 173px)" }}>
          {selectedArea && (
            <MarkDownComp
              MenuTitle={t("bankruptcy:tax_bankruptcy_practice_areas")}
              content={article?.content.rendered ?? ""}
              selectedMenuItem={selectedArea as StaticBoxProps}
              menuItems={menuItems as CustomMenuItemType[]}
              buttonText={
                t("bankruptcy:defaultChildButtonText") as DefaultTFuncReturn
              }
              buttonLink={"/new-clients-intake-overview"}
            />
          )}
        </div>
      </Spin>
      <Footer />
    </>
  );
}
